import Cookies from 'js-cookie';

// ─── Module-level cache ───────────────────────────────────────────────────────
// Shared across all hook instances so concurrent widgets reuse in-flight
// fetches instead of firing duplicate requests.
// Call clearAPIDataCache() on logout — see AuthContextProvider.
const data = {};
const lastUpdated = {};
const pendingFetches = {}; // uniqueKey → Promise, shared among concurrent callers

export const clearAPIDataCache = () => {
  for (const k in data) delete data[k];
  for (const k in lastUpdated) delete lastUpdated[k];
  for (const k in pendingFetches) delete pendingFetches[k];
  locationInquiry = '';
  if (locationInquiryReject) locationInquiryReject(new Error('Cache cleared'));
  locationReadyPromise = null;
  locationInquiryReject = null;
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
};

// ─── Location inquiry state ───────────────────────────────────────────────────
// One shared promise that all pending get() calls wait on while
// the location readiness check is in progress.
let locationInquiry = '';
let eventSource;
let locationReadyPromise = null;
let locationInquiryReject = null;
const FETCH_TIMEOUT_MS = 30 * 1000; // 30 seconds

// ─── Status constants ─────────────────────────────────────────────────────────
const STATUS_FETCHING = 'fetching';
const STATUS_ERROR = 'error';
const STATUS_SUCCESS = 'success';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const buildSafeFallbackData = () => ({ result: [], data: [], alerts: [] });

const buildInvalidResponseError = ({ key, location, response }) => {
  const status = response?.status;
  const statusLabel = Number.isFinite(status) ? ` (HTTP ${status})` : '';
  const message =
    'The server returned an empty or invalid response for this widget. Try refreshing. If it keeps happening, the data service may be temporarily unavailable.';
  const error = new Error(message);
  error.code = 'INVALID_API_RESPONSE';
  error.userMessage = message;
  error.details = `Route: ${key || 'unknown'} | Location: ${location || 'unknown'}${statusLabel}`;
  return error;
};

const extractResponseDataOrThrow = ({ response, key, location }) => {
  if (response === null || response === undefined || typeof response !== 'object') {
    throw buildInvalidResponseError({ key, location, response });
  }
  if (response.data === null || response.data === undefined) {
    throw buildInvalidResponseError({ key, location, response });
  }
  return response.data;
};

const normalizeError = (error) => {
  if (error?.code === 'INVALID_API_RESPONSE') return error;
  const e = error instanceof Error ? error : new Error(String(error));
  if (!e.userMessage) {
    e.userMessage =
      'We could not load data for this widget. Try refreshing. If the issue continues, the server may be unavailable.';
  }
  return e;
};

// Races a promise against a timeout — no setInterval or polling needed.
const withTimeout = (promise, ms, message) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => {
        const err = new Error(message);
        err.userMessage = message;
        reject(err);
      }, ms),
    ),
  ]);

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useAPIData = ({
  successCallback = () => {},
  errorCallback = () => {},
  fetchingCallback = () => {},
} = {}) => {
  const updateEntry = (uniqueKey, updateInfo) => {
    if (data[uniqueKey] === undefined) data[uniqueKey] = {};
    data[uniqueKey] = { ...data[uniqueKey], ...updateInfo };
  };

  // Returns a promise that resolves once the location inquiry is settled.
  // All concurrent get() callers share the same promise instead of each
  // spinning up their own setInterval.
  const waitForLocation = () => {
    if (locationInquiry === '' || locationInquiry === 'clear') return Promise.resolve();
    if (locationInquiry === 'failed') return Promise.reject(new Error('Location inquiry failed'));
    return locationReadyPromise ?? Promise.resolve();
  };

  const get = ({ key = '', apiFunction = () => {}, params = {} }) => {
    const location = `${Cookies.get('city')}-${Cookies.get('state')}`;
    const uniqueKey = `${location}-${key}`;

    return waitForLocation()
      .then(() => {
        // Already cached as success
        if (data[uniqueKey]?.state === STATUS_SUCCESS) {
          fetchingCallback();
          return new Promise((resolve) =>
            setTimeout(() => {
              successCallback();
              resolve({ ...data[uniqueKey] });
            }, 500),
          );
        }

        // Previously errored — surface without re-fetching
        if (data[uniqueKey]?.state === STATUS_ERROR) {
          errorCallback(data[uniqueKey]?.error?.userMessage || data[uniqueKey]?.error?.message);
          return Promise.resolve({ ...data[uniqueKey] });
        }

        // Fetch already in-flight — share the existing promise
        if (data[uniqueKey]?.state === STATUS_FETCHING && pendingFetches[uniqueKey]) {
          fetchingCallback();
          return pendingFetches[uniqueKey];
        }

        // Start a new fetch
        updateEntry(uniqueKey, { state: STATUS_FETCHING });
        lastUpdated[uniqueKey] = Date.now();

        const fetchPromise = withTimeout(
          apiFunction(params),
          FETCH_TIMEOUT_MS,
          'The request timed out. Try refreshing.',
        )
          .then((response) => {
            const responseData = extractResponseDataOrThrow({ response, key, location });
            updateEntry(uniqueKey, { state: STATUS_SUCCESS, data: responseData });
            successCallback();
            return { ...data[uniqueKey] };
          })
          .catch((error) => {
            const err = normalizeError(error);
            updateEntry(uniqueKey, {
              state: STATUS_ERROR,
              data: buildSafeFallbackData(),
              error: err,
            });
            errorCallback(err.userMessage || err.message);
            return { ...data[uniqueKey] };
          })
          .finally(() => {
            delete pendingFetches[uniqueKey];
          });

        pendingFetches[uniqueKey] = fetchPromise;
        return fetchPromise;
      })
      .catch((error) => {
        const err = normalizeError(error);
        errorCallback(err.userMessage || err.message);
        return { state: STATUS_ERROR, data: buildSafeFallbackData(), error: err };
      });
  };

  const cancelLocationInquiry = () => {
    locationInquiry = 'failed';
    if (eventSource) eventSource.close();
    locationInquiryReject?.(new Error('Location inquiry cancelled'));
    locationReadyPromise = null;
  };

  const forceGet = ({ key = '', apiFunction = () => {}, params = {} }) => {
    const location = `${Cookies.get('city')}-${Cookies.get('state')}`;
    const uniqueKey = `${location}-${key}`;

    // Return cached data if recent enough and successful
    if (
      lastUpdated[uniqueKey] &&
      Date.now() - lastUpdated[uniqueKey] < 1000 * 60 * 3 &&
      data[uniqueKey]?.state === STATUS_SUCCESS
    ) {
      return Promise.resolve({ ...data[uniqueKey] });
    }

    // Bust the cache entry and re-fetch via get()
    delete data[uniqueKey];
    delete pendingFetches[uniqueKey];
    return get({ key, apiFunction, params });
  };

  return { get, cancelLocationInquiry, forceGet };
};
